<template>
  <div class="relative" @keydown="onKeyDown" tabindex="0">
    <!-- Main Field -->
    <div
      class="relative w-full border border-gray-300 rounded-lg bg-white px-4 py-2.5 cursor-pointer transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-main focus:border-main hover:border-gray-400"
      :class="[{ 'ring-2 ring-main border-main': open }, SelectClass]"
      @click="toggleDropdown"
    >
      <!-- Multiple Selection -->
      <template v-if="multiple">
        <div class="flex flex-wrap gap-2 min-h-[20px]">
          <template v-if="modelValue && modelValue.length">
            <span
              v-for="(val, i) in modelValue"
              :key="i"
              class="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium select-none shadow-sm border transition-all duration-200"
              :class="{
                'bg-gradient-to-r from-main to-main-hover text-white border-main/20 hover:shadow-md': !isProtected(val),
                'bg-gray-400 text-white border-gray-400/20': isProtected(val)
              }"
            >
              <!-- Protected lock icon -->
              <svg v-if="isProtected(val)" class="w-3 h-3 text-white mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
              </svg>
              
              <span class="mr-2">{{ getLabel(val) }}</span>
              
              <!-- Remove button - only show for non-protected options -->
              <button 
                v-if="!isProtected(val)"
                class="flex items-center justify-center w-4 h-4 bg-white/20 hover:bg-white/30 rounded-full transition-colors duration-200" 
                @click.stop="removeValue(val)"
                title="Remove"
              >
                <svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                </svg>
              </button>
            </span>
          </template>
          <span v-else class="text-gray-500 text-sm">{{ placeholder }}</span>
        </div>
      </template>

      <!-- Single Selection -->
      <template v-else>
        <div class="flex items-center justify-between">
          <span class="text-gray-900 select-none" :class="{ 'text-gray-500': !modelValue }">
            {{ getLabel(modelValue) || placeholder }}
          </span>
        </div>
      </template>

      <!-- Chevron Icon -->
      <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg 
          class="w-5 h-5 text-gray-400 transition-transform duration-200" 
          :class="{ 'rotate-180': open }"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>
    </div>

    <!-- Desktop Dropdown -->
    <transition
      name="dropdown"
      enter-active-class="transition duration-300 ease-out transform-gpu"
      enter-from-class="opacity-0 -translate-y-2 scale-y-0"
      enter-to-class="opacity-100 translate-y-0 scale-y-100"
      leave-active-class="transition duration-200 ease-in transform-gpu"
      leave-from-class="opacity-100 translate-y-0 scale-y-100"
      leave-to-class="opacity-0 -translate-y-2 scale-y-0"
    >
      <div
        v-if="open && !isMobile"
        class="absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden origin-top"
        style="transform-origin: top center"
      >
        <!-- Search -->
        <div v-if="search" class="p-3 border-b border-gray-100">
          <div class="relative">
            <input
              v-model="query"
              type="text"
              :placeholder="searchPlaceholder"
              class="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-main-hover focus:border-main-hover text-sm transition-colors duration-200"
              @click.stop
            />
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
          </div>
        </div>

        <!-- Options Container -->
        <div class="py-1 overflow-auto max-h-52" ref="optionsContainer">
          <template v-if="computedOptions.length">
            <Option
              v-for="(opt, i) in filteredOptions"
              :key="i"
              :value="opt.value"
              :label="opt.label"
              :selected="isSelected(opt.value)"
              :isProtected="opt.isProtected || opt.protected || false"
              :focused="focusedIndex === i"
              :class="OptionsClass"
              @select="selectValue"
              @focus="focusedIndex = i"
              ref="options"
            />
          </template>

          <!-- Slot for custom options -->
          <slot v-else :isSelected="isSelected" @select="selectValue" />

          <!-- No results -->
          <div v-if="search && query && !filteredOptions.length" class="px-4 py-3 text-sm text-gray-500 text-center">
            {{ noResultsText }}
          </div>
        </div>
      </div>
    </transition>

    <!-- Mobile Modal -->
    <MobileModal v-model="open" v-if="isMobile">
      <!-- Mobile Header -->
      <div class="flex items-center justify-center p-4 border-b border-gray-200">
        <h3 class="text-lg font-medium text-gray-900">
          {{ placeholder }}
        </h3>
      </div>

      <!-- Mobile Search -->
      <div v-if="search" class="p-4 border-b border-gray-100">
        <div class="relative">
          <input
            v-model="query"
            type="text"
            :placeholder="searchPlaceholder"
            class="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-hover focus:border-main-hover text-base transition-colors duration-200"
            @click.stop
          />
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
        </div>
      </div>

      <!-- Mobile Options Container -->
      <div class="flex-1 overflow-auto">
        <template v-if="computedOptions.length">
          <div
            v-for="(opt, i) in filteredOptions"
            :key="i"
            class="px-6 py-4 border-b border-gray-100 last:border-b-0 cursor-pointer transition-all duration-200 text-base relative"
            :class="{
              'bg-blue-600 text-white shadow-sm': isSelected(opt.value),
              'hover:bg-gray-50 hover:shadow-sm': !isSelected(opt.value) && !(opt.protected || opt.isProtected),
              'text-gray-900': !isSelected(opt.value),
              'opacity-75 cursor-not-allowed': (opt.protected || opt.isProtected) && isSelected(opt.value),
              'bg-gray-100 text-gray-500 cursor-not-allowed': (opt.protected || opt.isProtected) && !isSelected(opt.value)
            }"
            @click="!(opt.protected || opt.isProtected) || !isSelected(opt.value) ? selectValue(opt.value) : null"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center flex-1 min-w-0">
                <!-- Selection indicator for mobile -->
                <div 
                  v-if="isSelected(opt.value)" 
                  class="mr-3 flex-shrink-0 w-5 h-5 bg-white/20 rounded-full flex items-center justify-center"
                >
                  <svg class="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                
                <!-- Protected indicator for mobile -->
                <div 
                  v-else-if="opt.protected || opt.isProtected" 
                  class="mr-3 flex-shrink-0 w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center"
                >
                  <svg class="h-3 w-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
                
                <div class="flex-1 min-w-0">
                  <span class="block truncate font-medium" :class="{ 
                    'text-white': isSelected(opt.value), 
                    'text-gray-500': (opt.protected || opt.isProtected) && !isSelected(opt.value),
                    'text-gray-900': !isSelected(opt.value) && !(opt.protected || opt.isProtected) 
                  }">
                    {{ opt.label }}
                  </span>
                </div>
              </div>
              
              <!-- Additional icon for selected/protected state -->
              <div v-if="isSelected(opt.value)" class="ml-3 flex-shrink-0">
                <div class="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                  <svg v-if="!(opt.protected || opt.isProtected)" class="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                  </svg>
                  <svg v-else class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Selected state glow effect for mobile -->
            <div 
              v-if="isSelected(opt.value)" 
              class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none"
            ></div>
          </div>
        </template>

        <!-- Slot for custom options -->
        <div v-else>
          <slot :isSelected="isSelected" @select="selectValue" />
        </div>

        <!-- No results -->
        <div v-if="search && query && !filteredOptions.length" class="px-6 py-8 text-base text-gray-500 text-center">
          {{ noResultsText }}
        </div>
      </div>
    </MobileModal>
  </div>
</template>

<script>
import Option from "@/components/Blocks/Option.vue"
import MobileModal from "@/components/Blocks/MobileModal.vue"

export default {
  name: "Select",
  components: { Option, MobileModal },
  props: {
    modelValue: { type: [String, Number, Object, Array, Boolean, null], default: null },
    options: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    search: { type: Boolean, default: false },
    placeholder: { type: String, default: "Select an option..." },
    searchPlaceholder: { type: String, default: "Search options..." },
    noResultsText: { type: String, default: "No results found" },
    allowEmpty: { type: Boolean, default: false },
    emptyLabel: { type: String, default: "No value" },
    SelectClass: { type: [String, Object, Array], default: "" },
    OptionsClass: { type: [String, Object, Array], default: "" },
    sortBy: { type: String, default: null }, // Key to sort by (e.g., 'label', 'value')
    sortDirection: { type: String, default: 'asc', validator: (value) => ['asc', 'desc'].includes(value) } // Sort direction
  },
  data() {
    return {
      open: false,
      query: "",
      focusedIndex: -1,
      windowWidth: window.innerWidth
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768 // Tailwind's md breakpoint
    },
    computedOptions() {
      let options = this.options;
      
      // Apply sorting if sortBy is specified
      if (this.sortBy) {
        options = [...options].sort((a, b) => {
          let aValue = a[this.sortBy];
          let bValue = b[this.sortBy];
          
          // Handle null/undefined values
          if (aValue == null && bValue == null) return 0;
          if (aValue == null) return this.sortDirection === 'asc' ? 1 : -1;
          if (bValue == null) return this.sortDirection === 'asc' ? -1 : 1;
          
          // Convert to strings for comparison if needed
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }
          
          // Compare values
          let comparison = 0;
          if (aValue < bValue) {
            comparison = -1;
          } else if (aValue > bValue) {
            comparison = 1;
          }
          
          // Apply sort direction
          return this.sortDirection === 'desc' ? -comparison : comparison;
        });
      }
      
      // Add empty option if allowed
      if (this.allowEmpty) {
        return [{ value: null, label: this.emptyLabel }, ...options];
      }
      
      return options;
    },
    filteredOptions() {
      if (!this.search || !this.query) return this.computedOptions
      return this.computedOptions.filter(opt =>
        (opt.label || "").toLowerCase().includes(this.query.toLowerCase())
      )
    }
  },
  created() {
    // Initialize selected values from options with selected: true
    this.initializeSelectedValues();
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    initializeSelectedValues() {
      // Only initialize if no value is already set
      if (this.modelValue === null || this.modelValue === undefined || 
          (Array.isArray(this.modelValue) && this.modelValue.length === 0)) {
        
        const preselectedOptions = this.options.filter(opt => opt.selected === true);
        
        if (preselectedOptions.length > 0) {
          if (this.multiple) {
            const values = preselectedOptions.map(opt => opt.value);
            this.$emit("update:modelValue", values);
          } else {
            // For single-select, take the first preselected option
            this.$emit("update:modelValue", preselectedOptions[0].value);
          }
        }
      }
    },
    toggleDropdown() {
      this.open = !this.open
      if (this.open) {
        this.focusedIndex = this.filteredOptions.findIndex(opt => this.isSelected(opt.value))
        if (this.focusedIndex === -1) this.focusedIndex = 0
        this.scrollToFocused()
        this.$nextTick(() => {
          if (this.search) {
            const input = this.$el.querySelector('input')
            if (input) input.focus()
          }
        })
      }
    },
    selectValue(val) {
      // Find the option to check if it's protected
      const option = this.filteredOptions.find(opt => opt.value === val);
      
      if (this.multiple) {
        // If empty value is selected in multiple mode, clear all selections
        if (val === null) {
          this.$emit("update:modelValue", [])
          this.$emit("change", [])
          return
        }
        
        const arr = Array.isArray(this.modelValue) ? [...this.modelValue] : []
        const index = arr.findIndex(item => item === val)
        
        if (index !== -1) {
          // Trying to deselect - check if it's protected
          if (option && (option.protected || option.isProtected)) {
            return; // Don't allow deselection of protected options
          }
          arr.splice(index, 1)
        } else {
          arr.push(val)
        }
        this.$emit("update:modelValue", arr)
        this.$emit("change", arr)
      } else {
        // For single-select, check if clicking already selected option
        if (this.modelValue === val) {
          // Already selected - check if it's protected
          if (option && (option.protected || option.isProtected)) {
            this.open = false
            return; // Don't allow deselection of protected single-select options
          }
          // Already selected - just close dropdown without emitting change
          this.open = false
          return
        }
        
        this.$emit("update:modelValue", val)
        this.open = false
        this.$emit("change", val)
      }
    },
    removeValue(val) {
      // Find the option to check if it's protected
      const option = this.computedOptions.find(opt => opt.value === val);
      
      if (this.multiple) {
        // Don't allow removal of protected options
        if (option && (option.protected || option.isProtected)) {
          return;
        }
        
        const arr = [...this.modelValue]
        const idx = arr.indexOf(val)
        if (idx !== -1) {
          arr.splice(idx, 1)
          this.$emit("update:modelValue", arr)
          this.$emit("change", arr)
        }
      } else {
        // Don't allow removal of protected single-select options
        if (option && (option.protected || option.isProtected)) {
          return;
        }
        
        this.$emit("update:modelValue", null)
        this.$emit("change", null)
      }
    },
    isProtected(val) {
      const option = this.computedOptions.find(opt => opt.value === val);
      return option && (option.protected || option.isProtected);
    },
    getLabel(val) {
      if (val === null || val === undefined) return ""
      const opt = this.computedOptions.find(o => {
        // Handle strict equality for all types including booleans
        return o.value === val
      })
      return opt ? opt.label : String(val)
    },
    isSelected(val) {
      if (this.multiple) {
        return Array.isArray(this.modelValue) && this.modelValue.includes(val)
      }
      return this.modelValue === val
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.open = false
      }
    },
    scrollToFocused() {
      this.$nextTick(() => {
        if (this.$refs.optionsContainer && this.$refs.options) {
          const container = this.$refs.optionsContainer
          const focusedOption = this.$refs.options[this.focusedIndex]
          
          if (focusedOption && focusedOption.$el) {
            const optionElement = focusedOption.$el
            const containerRect = container.getBoundingClientRect()
            const optionRect = optionElement.getBoundingClientRect()
            
            const isVisible = 
              optionRect.top >= containerRect.top && 
              optionRect.bottom <= containerRect.bottom
            
            if (!isVisible) {
              const scrollTop = optionElement.offsetTop - container.offsetTop - 
                (container.clientHeight / 2) + (optionElement.offsetHeight / 2)
              
              container.scrollTo({
                top: Math.max(0, scrollTop),
                behavior: 'smooth'
              })
            }
          }
        }
      })
    },
    onKeyDown(e) {
      if (!this.open) {
        if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
          this.open = true
          this.focusedIndex = 0
          this.scrollToFocused()
          e.preventDefault()
        }
        return
      }

      if (e.key === "ArrowDown") {
        if (this.focusedIndex < this.filteredOptions.length - 1) {
          this.focusedIndex++
          this.scrollToFocused()
        }
        e.preventDefault()
      } else if (e.key === "ArrowUp") {
        if (this.focusedIndex > 0) {
          this.focusedIndex--
          this.scrollToFocused()
        }
        e.preventDefault()
      } else if (e.key === "Enter") {
        const opt = this.filteredOptions[this.focusedIndex]
        if (opt) this.selectValue(opt.value)
        e.preventDefault()
      } else if (e.key === "Escape") {
        this.open = false
        e.preventDefault()
      } else if (e.key === "Backspace" || e.key === "Delete") {
        if (!this.search && this.multiple && this.modelValue?.length) {
          this.removeValue(this.modelValue[this.modelValue.length - 1])
          e.preventDefault()
        } else if (!this.search && !this.multiple) {
          this.removeValue(this.modelValue)
          e.preventDefault()
        }
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    }
  }
}
</script>

<style scoped>
@keyframes slide-up {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-slide-up {
  animation: slide-up 0.3s ease-out forwards;
}

/* Mobile modal transition classes */
.mobile-modal-enter-active {
  transition: opacity 0.3s ease-out;
}

.mobile-modal-enter-from {
  opacity: 0;
}

.mobile-modal-enter-to {
  opacity: 1;
}

.mobile-modal-leave-active {
  transition: opacity 0.2s ease-in;
}

.mobile-modal-leave-from {
  opacity: 1;
}

.mobile-modal-leave-to {
  opacity: 0;
}

/* Drag handle styling */
.cursor-grab:hover .drag-handle {
  background-color: #9ca3af;
  transform: scaleY(1.2);
}

.cursor-grab:focus-within .drag-handle {
  background-color: var(--color-main, #3b82f6);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  transform: scaleY(1.2);
}

.cursor-grabbing .drag-handle {
  background-color: #6b7280;
  transform: scaleY(1.4);
}
</style>
